import { useState } from 'react'
import { X, Edit2, Plus, Home, Phone, Briefcase } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select'
import {
  InfluencerData,
  SocialMediaInfluencer,
} from '../../../../core/interface/chronique.interface'
import {
  InfluencerForInfluencedBookStatusEnum,
  InfluencerStatsEnum,
} from '../../../../core/enums/influencer-status.enum'
import { cn } from '../../../../lib/utils'
import UpdateInfluencer from '../../../../core/api/services/bookmetrie/private-reports/chroniqueur/put-influencer'
import UpdateInfluencerOnInfluencedBook from '../../../../core/api/services/bookmetrie/private-reports/chroniqueur/put-influencer-on-influenced-book'
import CreateInfluencerSocialMedia from '../../../../core/api/services/bookmetrie/private-reports/chroniqueur/post-influencer-social-media'
import InfluencerNoteModal from './InfluencerNoteModal'
import UpdateInfluencerSocialMedia from '../../../../core/api/services/bookmetrie/private-reports/chroniqueur/put-influencer-social-media'
import InfluencerSocialMediaModal from './InfluencerSocialMediaModal'
import DeleteInfluencerSocialMedia from '../../../../core/api/services/bookmetrie/private-reports/chroniqueur/delete-influencer-social-media'
import socialIcons from '../../../../core/utils/media-platform.utils'
import statusColors from '../../../../core/utils/mapping-status-influencer-color.util'
import { TableViewTypeInfluencerEnum } from '../../../../core/enums/table-view-type-influencer.enum'
import { ReactComponent as BookIcon } from '../../../../assets/icons/app/book.icon.svg'
import { ReactComponent as CommentIcon } from '../../../../assets/icons/comment-icon.svg'

interface Props {
  viewType: TableViewTypeInfluencerEnum
  chroniqueurId?: string
  user: InfluencerData | null
  onClose: () => void
  onUpdateUser: (user: InfluencerData) => void
}

const InfluencerDetailsContainer = ({
  viewType,
  chroniqueurId,
  user,
  onClose,
  onUpdateUser,
}: Props) => {
  const { t } = useTranslation()
  const [showAddSocialModal, setShowAddSocialModal] = useState(false)
  const [showEditSocialModal, setShowEditSocialModal] = useState(false)
  const [selectedSocialMedia, setSelectedSocialMedia] =
    useState<SocialMediaInfluencer | null>(null)
  const [showNoteModal, setShowNoteModal] = useState(false)
  const [tempNote, setTempNote] = useState(user?.note || '')
  if (!user) return null

  const handleInputChange = (
    field: string,
    value: string | number | boolean,
  ) => {
    if (field.startsWith('influencer.')) {
      const influencerField = field.replace('influencer.', '')
      void UpdateInfluencer(user.influencer.userid, {
        [influencerField]: value,
      }).then((response) => {
        if (response?.data) {
          onUpdateUser({
            ...user,
            influencer: {
              ...user.influencer,
              [influencerField]: value,
            },
          })
        }
      })
    } else {
      if (!chroniqueurId) return
      void UpdateInfluencerOnInfluencedBook(
        chroniqueurId,
        user.influencer.userid,
        {
          [field]: value,
        },
      ).then((response) => {
        if (response?.data) {
          onUpdateUser({
            ...user,
            [field]: value,
          })
        }
      })
    }
  }

  const handleCreateSocialMedia = (url: string, followers: number) => {
    void CreateInfluencerSocialMedia(user.influencer.userid, {
      url,
      followers,
    }).then((response) => {
      if (response?.data) {
        const newSocialMedia = response.data
        onUpdateUser({
          ...user,
          influencer: {
            ...user.influencer,
            social_media: [...user.influencer.social_media, newSocialMedia],
          },
        })
        setShowAddSocialModal(false)
      }
    })
  }

  const handleUpdateSocialMedia = (
    social_media_id: string,
    url: string,
    followers: number,
  ) => {
    void UpdateInfluencerSocialMedia(user.influencer.userid, social_media_id, {
      url,
      followers,
    }).then((response) => {
      if (response?.data) {
        const updatedSocialMedia = response.data
        const updatedSocialMediaList = user.influencer.social_media.map((sm) =>
          sm.social_media.platform === updatedSocialMedia.social_media.platform
            ? updatedSocialMedia
            : sm,
        )
        onUpdateUser({
          ...user,
          influencer: {
            ...user.influencer,
            social_media: updatedSocialMediaList,
          },
        })
        setShowEditSocialModal(false)
      }
    })
  }

  const handleDeleteSocialMedia = (social_media_id: string) => {
    void DeleteInfluencerSocialMedia(
      user.influencer.userid,
      social_media_id,
    ).then(() => {
      const updatedSocialMediaList = user.influencer.social_media.filter(
        (sm) => sm.id.toString() !== social_media_id,
      )
      onUpdateUser({
        ...user,
        influencer: {
          ...user.influencer,
          social_media: updatedSocialMediaList,
        },
      })
      setShowEditSocialModal(false)
    })
  }

  return (
    <>
      <div className="w-96 bg-white border-l border-gray-200 flex flex-col">
        <div className="bg-gray-600 text-white p-6 relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:bg-gray-700 px-3 py-1"
          >
            <X className="h-4 w-4 mr-1" />
            {t('commons.buttons.close')}
          </Button>

          <div className="pr-20">
            <h3 className="text-xl font-bold text-white mb-1">
              {user.influencer.first_name} {user.influencer.last_name}
            </h3>
            <div className="text-blue-400 text-sm mb-2">
              {user.influencer.pseudonym}
            </div>
            <div className="text-sm text-white mb-2">
              {t(
                user.influencer.followers > 1
                  ? 'chroniqueurView.follows_other'
                  : 'chroniqueurView.follows_one',
                { count: user.influencer.followers },
              )}
            </div>
            <div className="text-sm text-gray-300 mb-4">
              {user.influencer.email}
            </div>

            {/* Status Dropdowns */}
            <div className="space-y-2 text-primary">
              <Select
                value={user.influencer.status || ''}
                onValueChange={(value) =>
                  handleInputChange('influencer.status', value)
                }
                disabled={
                  viewType === TableViewTypeInfluencerEnum.NEW_CHRONIQUEUR_VIEW
                }
              >
                <SelectTrigger
                  className={
                    statusColors[user.influencer.status] ||
                    'bg-gray-100 text-gray-800 border-gray-200'
                  }
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {InfluencerStatsEnum &&
                    Object.values(InfluencerStatsEnum).map((status) => (
                      <SelectItem
                        key={status}
                        value={status}
                        className={cn(
                          'border-b border-white',
                          statusColors[status] ||
                            'bg-gray-100 text-gray-800 border-gray-200',
                        )}
                      >
                        {t(`chroniqueurView.status.${status}`)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>

              <Select value={user.current_influencedbook_status ?? ''} disabled>
                <SelectTrigger
                  className={
                    statusColors[
                      user.current_influencedbook_status ||
                        InfluencerForInfluencedBookStatusEnum.UNSELECTED
                    ]
                  }
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {InfluencerForInfluencedBookStatusEnum &&
                    Object.values(InfluencerForInfluencedBookStatusEnum).map(
                      (status) => (
                        <SelectItem
                          key={status}
                          value={status}
                          className={cn(
                            'border-b border-white',
                            statusColors[status] ||
                              'bg-gray-100 text-gray-800 border-gray-200',
                          )}
                        >
                          {t(`chroniqueurView.chronique.${status}`)}
                        </SelectItem>
                      ),
                    )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="flex-1 p-6 overflow-auto">
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                {t('chroniqueurView.generalInfo')}
              </h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <BookIcon className="h-4 w-4" />
                  <span>
                    {user.influencer.library_size}{' '}
                    {t('chroniqueurView.booksOnGleeph')}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <CommentIcon className="h-4 w-4" />
                  <span>
                    {user.influencer.gleeph_reviews}{' '}
                    {t('chroniqueurView.commentsOnGleeph')}
                  </span>
                </div>
                {user.influencer.address && (
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <Home className="h-4 w-4" />
                    <span>{user.influencer.address}</span>
                  </div>
                )}
                {user.influencer.phone_number && (
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <Phone className="h-4 w-4" />
                    <span>{user.influencer.phone_number}</span>
                  </div>
                )}
                {user.influencer.profession && (
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <Briefcase className="h-4 w-4" />
                    <span>{user.influencer.profession}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Ses chroniques */}
            {/* {user.chroniques_done && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Ses chroniques
                </h4>

                {user.chroniques_done.map((chronique) => (
                  <div
                    key={chronique.ean}
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="w-12 h-16 bg-red-100 rounded flex-shrink-0">
                      <img
                        src={chronique.cover || '/placeholder.svg'}
                        alt="Book cover"
                        className="w-full h-full object-cover rounded"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        {chronique.title}
                      </div>
                      <div className="text-xs text-gray-500">
                        {chronique.publisher}
                      </div>
                      <div className="text-xs text-gray-500">
                        EAN {chronique.ean}
                      </div>
                      <div className="text-xs text-gray-500">
                        Date chronique : {chronique.date_chronique}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="w-5 h-5 bg-gray-800 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">X</span>
                        </div>
                        <div className="w-5 h-5 bg-orange-500 rounded-full" />
                        <div className="w-5 h-5 bg-blue-500 rounded-full" />
                        <div className="w-5 h-5 bg-blue-600 rounded-full" />
                        <div className="w-5 h-5 bg-pink-500 rounded-full" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )} */}

            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                {t('chroniqueurView.hisSocialNetworks')}
              </h4>
              {user.influencer.social_media.map((social) => {
                const Icon = socialIcons[social.social_media.platform]
                return (
                  <div
                    key={social.social_media.platform}
                    className="relative flex items-center gap-2 mb-2"
                  >
                    <Icon className="h-5 w-5 text-gray-600" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedSocialMedia(social)
                        setShowEditSocialModal(true)
                      }}
                      className="absolute -top-1 -right-1 h-6 w-6 p-0 bg-gray-200 hover:bg-gray-300"
                      disabled={
                        viewType ===
                        TableViewTypeInfluencerEnum.NEW_CHRONIQUEUR_VIEW
                      }
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                    <a
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {social.url}
                    </a>
                    <span className="text-sm text-gray-600">
                      {t(
                        social.followers > 1
                          ? 'chroniqueurView.follows_other'
                          : 'chroniqueurView.follows_one',
                        { count: social.followers },
                      )}
                    </span>
                  </div>
                )
              })}
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => setShowAddSocialModal(true)}
                disabled={
                  viewType === TableViewTypeInfluencerEnum.NEW_CHRONIQUEUR_VIEW
                }
              >
                <Plus className="h-4 w-4 mr-1" />
                {t('chroniqueurView.addSocialNetwork')}
              </Button>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-900">
                  {t('chroniqueurView.noteOnInfluencer')}
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowNoteModal(true)}
                  className="text-gray-600 hover:text-gray-800"
                  disabled={
                    viewType ===
                    TableViewTypeInfluencerEnum.NEW_CHRONIQUEUR_VIEW
                  }
                >
                  <Edit2 className="h-3 w-3" />
                </Button>
              </div>

              <div className="text-sm text-gray-600 mb-2">
                {user.note ? user.note : t('chroniqueurView.noNoteYet')}
              </div>
            </div>
          </div>
        </div>
      </div>

      <InfluencerSocialMediaModal
        action="add"
        showSocialModal={showAddSocialModal}
        setShowSocialModal={setShowAddSocialModal}
        handleCreateSocialMedia={handleCreateSocialMedia}
      />

      <InfluencerSocialMediaModal
        action="edit"
        selectedSocialMedia={selectedSocialMedia}
        showSocialModal={showEditSocialModal}
        setShowSocialModal={setShowEditSocialModal}
        handleUpdateSocialMedia={handleUpdateSocialMedia}
        handleDeleteSocialMedia={handleDeleteSocialMedia}
      />

      <InfluencerNoteModal
        showNoteModal={showNoteModal}
        setShowNoteModal={setShowNoteModal}
        tempNote={tempNote}
        setTempNote={setTempNote}
        handleInputChange={handleInputChange}
      />
    </>
  )
}

export default InfluencerDetailsContainer
